// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShieldAlert, Loader2, Send, Phone, Sparkles, Check, X, Copy,
  Building2, Layers, Wallet, Users as UsersIcon, ShieldCheck, ArrowRight,
  UserPlus, KeyRound, Pencil,
} from 'lucide-react';
import {
  listClientProblems, fetchScopeState, issueScopeShareToken, scheduleSectionCall,
  fetchProblemSchema, confirmProblemField,
  type ClientProblem, type ProblemFieldDef,
} from '../../orbit/lib/scopeApi';
import { ClarityHeader } from './ClarityHeader';
import { agentsApi } from '../../services/agentsApi';
import { requestAgentBridgeUrl, requestProblemWorkspaceScope } from '../../services/api';
import { fmtDate } from '../../components/dossier/fieldKit';

// Deep-scoping sections that need stakeholder input (Phase 1). Roles mirror
// the backend _SECTION_DEFAULT_ROLE so "who's needed" is accurate.
const SECTIONS = [
  { letter: 'h', role: 'CEO',        icon: Building2,   blurb: 'Strategy, pressure, what AI must not break.' },
  { letter: 'i', role: 'CTO',        icon: Layers,      blurb: 'Tools, access, security, integrations.' },
  { letter: 'm', role: 'CFO',        icon: Wallet,      blurb: 'Budget, approvals, procurement.' },
  { letter: 'n', role: 'Sponsor',    icon: UsersIcon,   blurb: 'Champion, economic buyer, sign-off.' },
  { letter: 'o', role: 'IT / Legal', icon: ShieldCheck, blurb: 'Compliance, data residency, security review.' },
];

// How the canonical problem-card fields are grouped for reading. Only the
// GROUPING lives here — labels, kinds and options come from
// /api/scope/problem-schema, which serves problem_card_fields.py directly.
//
// The previous version hardcoded its own fourteen-key list, and it had drifted
// badly: eight of those keys (`problem_categories`, `audience_facing`,
// `solution_type_intent`, `deployment_surface`, `data_sources_required`,
// `trigger_event`, `existing_app_to_extend`, `previously_attempted`) are
// section-E keys that never exist on a problem card, so they rendered
// "Pending" forever, while ten real problem fields were never shown at all.
// Section E now reaches this page properly — the plugin lane projects it onto
// the card (internal_orbit.py) the same way the native seed always did.
//
// Any schema field not named below still renders, under "Also captured", so a
// new field added in Python appears here without a frontend change.
const FIELD_GROUPS: Array<{ n: string; title: string; icon: any; keys: string[] }> = [
  { n: '01', title: 'Problem shape', icon: Building2, keys: [
    'problem_categories', 'affected_departments', 'affected_headcount', 'audience_facing',
  ]},
  { n: '02', title: 'Current state → desired state', icon: Layers, keys: [
    'current_state', 'desired_state', 'desired_constraints', 'constraints_detail',
  ]},
  { n: '03', title: 'Success & acceptance', icon: ShieldCheck, keys: [
    'success_metric', 'baseline', 'deadline', 'acceptance',
  ]},
  { n: '04', title: 'Solution & delivery', icon: Wallet, keys: [
    'solution_type_intent', 'deployment_surface', 'data_sources_required',
    'trigger_event', 'existing_app_to_extend', 'previously_attempted',
  ]},
];

// The hero, rendered above the cards rather than inside one.
const HERO_KEY = 'statement';

const INPUT_TYPE: Record<string, string> = { number: 'number', date: 'date' };

const fmtVal = (v: unknown, kind?: string): string => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (kind === 'date') return fmtDate(v);
  return String(v);
};

const SOURCE_LABEL = (s: string): string => {
  if (!s) return '';
  if (s.startsWith('delegate')) return 'shared link';
  if (s.startsWith('voice')) return 'a scheduled call';
  if (s.startsWith('derived') || s.startsWith('ai')) return 'Orbit';
  return 'your team';
};

/**
 * One captured field, editable in place.
 *
 * The page used to be a read-only artifact — "editing happens by re-scoping
 * with Orbit". That is a fine rule for a field nobody has captured and a bad
 * one for a field captured slightly wrong: reopening a scoping conversation to
 * change one word is a tax on the consultant reviewing the record. Saving goes
 * through the SAME confirm-field endpoint the chat surface uses, with
 * action='correct', so the field-state audit trail stays honest about who
 * changed what.
 */
const EditableField: React.FC<{
  def: ProblemFieldDef;
  value: unknown;
  onSave: (value: unknown) => Promise<void>;
}> = ({ def, value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any>(value ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);

  const display = fmtVal(value, def.kind).trim();
  const opts = def.options || [];
  const labelFor = (v: string) => opts.find(o => o.value === v)?.label || v;
  // Selects store the option VALUE but should read as its label.
  const shown = opts.length
    ? (Array.isArray(value) ? value.map(v => labelFor(String(v))).join(', ') : (display ? labelFor(display) : ''))
    : display;

  const commit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSave(def.kind === 'number' ? (draft === '' ? null : Number(draft)) : draft);
      setEditing(false);
    } catch {
      setErr('Could not save — try again.');
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full px-2.5 py-1.5 rounded-lg border border-brand-300 bg-white text-[13px] outline-none focus:border-brand-500';

  if (editing) {
    return (
      <div className="py-2 border-t border-gray-200/40 first:border-t-0">
        <div className="text-[10px] font-sans uppercase tracking-wider text-gray-400 mb-1">{def.label}</div>
        {def.kind === 'longtext' ? (
          <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={4}
            placeholder={def.placeholder || ''} className={`${inputCls} resize-y`} />
        ) : def.kind === 'select' ? (
          <select autoFocus value={draft} onChange={e => setDraft(e.target.value)} className={inputCls}>
            <option value="">Not set</option>
            {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : def.kind === 'multiselect' ? (
          <div className="flex flex-wrap gap-1.5 py-1">
            {opts.map(o => {
              const arr: string[] = Array.isArray(draft) ? draft : [];
              const on = arr.includes(o.value);
              return (
                <button key={o.value} type="button"
                  onClick={() => setDraft(on ? arr.filter(x => x !== o.value) : [...arr, o.value])}
                  className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors ${on
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300'}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        ) : (
          <input autoFocus type={INPUT_TYPE[def.kind] ?? 'text'}
            value={def.kind === 'date' && typeof draft === 'string' ? draft.slice(0, 10) : draft}
            onChange={e => setDraft(e.target.value)} placeholder={def.placeholder || ''}
            className={inputCls} />
        )}
        {def.why && <p className="text-[11px] text-gray-400 mt-1.5">{def.why}</p>}
        {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={commit} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 whitespace-nowrap">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
          </button>
          <button type="button" onClick={() => { setEditing(false); setErr(null); }}
            className="text-[12px] font-medium px-3 py-1.5 rounded-full text-gray-500 hover:text-gray-900">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group py-2 border-t border-gray-200/40 first:border-t-0">
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] font-sans uppercase tracking-wider text-gray-400">{def.label}</span>
        {def.critical && <span className="text-[10px] text-amber-500" title="Critical for the build spec">★</span>}
        <button type="button" onClick={() => setEditing(true)}
          title={`Edit ${def.label.toLowerCase()}`}
          className="ml-auto opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-brand-500">
          <Pencil className="w-3 h-3" />
        </button>
      </div>
      {shown ? (
        <div className="text-[13.5px] leading-snug text-gray-800 mt-0.5 whitespace-pre-wrap">{shown}</div>
      ) : (
        <button type="button" onClick={() => setEditing(true)}
          className="text-[12.5px] text-gray-300 italic mt-0.5 hover:text-brand-500 transition-colors">
          Pending — add it
        </button>
      )}
    </div>
  );
};

const ProblemDetail: React.FC = () => {
  const { problemId } = useParams();
  const navigate = useNavigate();
  const clientId = localStorage.getItem('client_id') || '';
  const [problem, setProblem] = useState<ClientProblem | null>(null);
  const [scope, setScope] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState<{ letter: string; role: string } | null>(null);
  const [callFor, setCallFor] = useState<{ letter: string; role: string } | null>(null);
  // The canonical problem-card schema, served from problem_card_fields.py. The
  // page renders whatever the schema says exists rather than its own list, so a
  // field added in Python shows up here without a frontend change.
  const [schema, setSchema] = useState<ProblemFieldDef[]>([]);
  const [openingWorkspace, setOpeningWorkspace] = useState(false);

  // Scoping happens in the WORKSPACE now — the primary action opens this
  // problem's dedicated scoping agent on the builder in a new tab (created
  // once via the provisioning bridge, reused forever; lands on the project
  // once the PRD births it). The in-page Orbit drawer stays only as the
  // fallback when the bridge cannot be built, so the customer is never
  // stranded on a dead button.
  const scopeInWorkspace = useCallback(async () => {
    if (openingWorkspace || !problemId) return;
    setOpeningWorkspace(true);
    try {
      const res = await requestProblemWorkspaceScope(problemId);
      const bridge = await requestAgentBridgeUrl(res.agent_path, res.client_id || clientId || undefined);
      if (bridge?.url) {
        window.open(bridge.url, '_blank', 'noopener,noreferrer');
        return;
      }
      throw new Error('bridge unavailable');
    } catch (err) {
      console.warn('[problem] workspace scoping unavailable, falling back to the Orbit drawer', err);
      window.dispatchEvent(new CustomEvent('orbit:open', {
        detail: { route: 'problem', context: `problem:${problemId}`, fresh: false },
      }));
    } finally {
      setOpeningWorkspace(false);
    }
  }, [openingWorkspace, problemId, clientId]);

  useEffect(() => {
    let cancelled = false;
    fetchProblemSchema()
      .then(f => { if (!cancelled) setSchema(f); })
      .catch(() => { /* the cards fall back to whatever is already captured */ });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!clientId) return;
    // Only the INITIAL load shows the full-page spinner. Background refreshes
    // (after each Orbit turn) must be SILENT — toggling `loading` swapped the
    // whole page to the spinner and back on every turn, which looked like the
    // page "refreshing" each time Orbit answered.
    if (!opts?.silent) setLoading(true);
    try {
      const all = await listClientProblems(clientId);
      const p = all.find(x => x.id === problemId) || null;
      setProblem(p);
      if (p?.engagement_id) {
        try { setScope(await fetchScopeState(p.engagement_id)); } catch { setScope(null); }
      }
    } finally { if (!opts?.silent) setLoading(false); }
  }, [clientId, problemId]);

  useEffect(() => { load(); }, [load]);

  // The global Orbit drawer emits 'orbit:after-turn' after each turn; refresh
  // this problem's view QUIETLY (silent — no full-page spinner) so captured
  // fields / status stay current without the page flashing.
  useEffect(() => {
    const onTurn = () => { load({ silent: true }); };
    window.addEventListener('orbit:after-turn', onTurn);
    return () => window.removeEventListener('orbit:after-turn', onTurn);
  }, [load]);

  // The actual VALUES collected for a deep section (label + value), so the
  // report shows WHAT was captured, not just a progress bar.
  const sectionCaptured = useCallback((letter: string): Array<{ label: string; value: string }> => {
    if (!scope) return [];
    const out: Array<{ label: string; value: string }> = [];
    for (const f of (scope.schema || [])) {
      if (!f.key?.startsWith(`${letter}.`) || !scope.visible_keys?.includes(f.key)) continue;
      const st = scope.l2_field_states?.[f.key]?.state;
      if (!['user_confirmed', 'user_corrected'].includes(st)) continue;
      const sub = f.key.slice(letter.length + 1);
      const val = fmtVal(scope.l2_sections?.[letter]?.[sub]).trim();
      if (val) out.push({ label: f.label, value: val });
    }
    return out;
  }, [scope]);

  // per-section completion + attribution from the engagement's L2 state
  const sectionStat = useCallback((letter: string) => {
    if (!scope) return { total: 0, filled: 0, sources: [] as string[] };
    const fields = (scope.schema || []).filter((f: any) => f.key?.startsWith(`${letter}.`) && scope.visible_keys?.includes(f.key));
    const states = scope.l2_field_states || {};
    let filled = 0; const sources = new Set<string>();
    for (const f of fields) {
      const st = states[f.key];
      if (st && ['user_confirmed', 'user_corrected'].includes(st.state)) {
        filled++;
        if (st.source) sources.add(SOURCE_LABEL(st.source));
      }
    }
    return { total: fields.length, filled, sources: [...sources] };
  }, [scope]);

  if (loading) {
    return <div className="flex items-center justify-center py-20 gap-3 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Loading…</span></div>;
  }
  if (!problem) {
    /*
     * Not "not found". This page reads the list of problems the caller may
     * see and looks for the id in it, so an empty result means one of two
     * things: the card does not exist, or it exists and is not open to this
     * person. A scoping card stays in the Solution Partner's working space
     * until they share it, and until that happens the only people who can
     * open it are the partner and whoever the work was assigned to.
     *
     * Saying "not found" sent people looking for something deleted, when the
     * truthful answer is that it is somebody else's to share.
     */
    return (
      <div className="py-16 text-center">
        <p className="text-gray-600">This problem is not open to you.</p>
        <p className="mx-auto mt-2 max-w-[46ch] text-sm text-gray-500">
          A scoping card stays with your Solution Partner until they share it. If the work was
          assigned to you, it appears here once that happens.
        </p>
        <button onClick={() => navigate('/projects')} className="mt-3 text-brand-500 text-sm font-medium">Back to Projects</button>
      </div>
    );
  }

  const deep = !!(problem.data && (problem.data.needs_deep_scoping || problem.data.deep_scoping));
  const reason = problem.data?.deep_reason || problem.data?.compliance_note || '';
  // Completed (PRD made) → scoping is done; hide the scope/call/link actions.
  const completed = ['PRD_READY', 'COMPLETED'].includes((problem.status || '').toUpperCase());

  // The phase's blocks to offer in the Schedule/Send dropdown — only when the
  // clicked target is itself a deep-scope block (not the problem 'e' itself).
  // Lets the recipient be sent the whole phase or one specific block.
  const deepBlocksFor = (l: string) => {
    if (!deep || !SECTIONS.some(s => s.letter === l)) return undefined;
    return SECTIONS.map(s => ({
      letter: s.letter,
      role: s.role,
      label: scope?.section_labels?.[s.letter] || s.letter.toUpperCase(),
    }));
  };

  return (
    <div className="space-y-6 max-w-[1100px]">
      <button onClick={() => navigate('/projects')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /> Projects
      </button>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-sans uppercase tracking-wider text-brand-500 font-semibold">Problem · scoped by Orbit</div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1 leading-snug">{problem.title}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-50 text-brand-600">
              {problem.status === 'PRD_READY' ? 'PRD ready' : problem.status === 'SCOPED' ? 'Scoped' : 'Scoping'}
            </span>
          </div>
        </div>
        {/* Problem-level actions — available in L1 too, not just inside the
            deep-scoping blocks. Scope with Orbit (resumes this problem),
            Schedule a call (Orbit calls to scope it), or Send a link (a
            teammate fills it). Target the problem itself (Section E). */}
        {/* Scoping actions — hidden once the problem is COMPLETED (PRD made):
            there's nothing left to scope. */}
        {!completed && (
        <div className="shrink-0 flex items-center gap-2">
          {/* Scope with Orbit — pulled from problem details for now (2026-08-22).
              scopeInWorkspace/openingWorkspace stay wired; uncomment to restore.
          <button
            type="button"
            disabled={openingWorkspace}
            onClick={scopeInWorkspace}
            title="Opens this problem's scoping agent in your workspace (new tab)"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-full text-sm font-semibold hover:bg-brand-600 transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            {openingWorkspace ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Scope with Orbit
          </button>
          */}
          <button
            type="button"
            onClick={() => setCallFor({ letter: 'e', role: 'your team' })}
            title="Orbit calls to scope this problem"
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-white ring-1 ring-gray-200 text-gray-700 rounded-full text-sm font-semibold hover:ring-brand-300 transition-colors whitespace-nowrap"
          >
            <Phone className="w-4 h-4" /> Schedule a call
          </button>
          <button
            type="button"
            onClick={() => setShare({ letter: 'e', role: 'your team' })}
            title="Send a link so a teammate can help scope this problem"
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-white ring-1 ring-gray-200 text-gray-700 rounded-full text-sm font-semibold hover:ring-brand-300 transition-colors whitespace-nowrap"
          >
            <Send className="w-4 h-4" /> Send a link
          </button>
        </div>
        )}
      </div>

      {/* Clarity-forward header — Orbit's read of this problem (score, verdict,
          dimensions, compliance flags) + the decision/next-step. The whole
          point of Orbit is clarity, so it leads. */}
      {problem.gate && (
        <ClarityHeader
          gate={problem.gate}
          problemId={problemId!}
          prdReady={completed}
          projectId={problem.data?.validx_project_id ?? null}
        />
      )}

      {/* Why deep scoping */}
      {deep && (
        <div className="flex items-start gap-3 rounded-2xl p-4 bg-accent-500/10 border border-accent-500/40">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-[14px] text-gray-900">This needs deep scoping</div>
            <p className="text-[13px] text-gray-600 mt-0.5">
              {reason || 'Orbit flagged this problem for proper scoping before a build — it spans strategy, tools, budget, sign-off, or compliance.'} Fill the blocks below yourself, or send each to the right person.
            </p>
          </div>
        </div>
      )}

      {/* Conditional: deep-scoping blocks ONLY when this problem was flagged
          for deep scoping. Otherwise the L1 scoping fields, grouped in cards. */}
      {!deep && (() => {
        const byKey = new Map(schema.map(f => [f.key, f]));
        const grouped = new Set<string>([HERO_KEY]);
        const groups = FIELD_GROUPS.map(g => {
          const defs = g.keys.map(k => byKey.get(k)).filter(Boolean) as ProblemFieldDef[];
          defs.forEach(d => grouped.add(d.key));
          return { ...g, defs };
        }).filter(g => g.defs.length > 0);
        // Anything the schema has that no group claims still renders — a field
        // added in Python must never silently vanish from this page again.
        const ungrouped = schema.filter(f => !grouped.has(f.key));
        const allGroups = ungrouped.length
          ? [...groups, { n: String(groups.length + 1).padStart(2, '0'), title: 'Also captured', icon: Sparkles, defs: ungrouped }]
          : groups;

        const heroDef = byKey.get(HERO_KEY);
        const statementVal = fmtVal(problem.data?.statement).trim();
        // Counted off the SCHEMA, so the badge can no longer disagree with what
        // is actually on the card.
        const totalCount = schema.length || 1;
        const filledCount = schema.filter(f =>
          fmtVal(problem.data?.[f.key.replace(/^p\./, '')]).trim()).length;

        // Report keys are stored bare on problem_card.data; field STATES are
        // keyed `p.<key>`, so send the card-vocabulary form.
        const saveField = async (def: ProblemFieldDef, value: unknown) => {
          await confirmProblemField(problemId!, `p.${def.key}`, value, 'correct');
          await load({ silent: true });
        };

        return (
          <div>
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="text-[17px] font-semibold text-gray-900 tracking-tight">Scoping summary</h2>
                <p className="text-[12.5px] text-gray-400 mt-0.5">What Orbit captured for this problem, written up like a scoping meeting.</p>
              </div>
              <span className="text-[11px] font-sans uppercase tracking-wider text-gray-400 tabular-nums shrink-0">
                {filledCount} / {totalCount} captured
              </span>
            </div>

            {/* Problem summary — the hero, NOT a card. Yellow accent rail +
                black eyebrow (brand), the statement in large near-black type. */}
            <div className="group relative pl-4 mb-7">
              <span className="absolute left-0 top-1 bottom-1 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              <div className="text-[10px] font-sans uppercase tracking-[0.16em] font-semibold text-gray-900">The problem</div>
              {heroDef ? (
                <div className="max-w-[760px] -mt-1">
                  <EditableField def={{ ...heroDef, label: 'Problem statement' }}
                    value={problem.data?.statement}
                    onSave={v => saveField(heroDef, v)} />
                </div>
              ) : statementVal ? (
                <p className="text-[18px] leading-relaxed text-gray-900 font-medium mt-1.5 max-w-[760px]">{statementVal}</p>
              ) : (
                <p className="text-[14px] text-gray-400 italic mt-1.5">Pending — Orbit captures this first.</p>
              )}
            </div>

            {/* Branded report cards — full width on mobile, two-up on desktop. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {allGroups.map(g => {
                const Icon = g.icon;
                return (
                  <section key={g.n} className="rounded-2xl bg-brand-50 p-5 ring-1 ring-gray-200/60">
                    <header className="mb-3 flex items-center gap-2.5 border-b border-gray-200/50 pb-2.5">
                      <span className="font-sans text-[11px] font-semibold tracking-[0.14em] text-brand-500">{g.n}</span>
                      <span className="text-brand-500/80"><Icon size={14} strokeWidth={2} /></span>
                      <h3 className="text-sm font-semibold text-gray-900">{g.title}</h3>
                    </header>
                    <div>
                      {g.defs.map(def => (
                        <EditableField key={def.key} def={def}
                          value={problem.data?.[def.key.replace(/^p\./, '')]}
                          onSave={v => saveField(def, v)} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            <div className="mt-5 flex items-center gap-2 text-[12.5px] text-gray-500">
              <Sparkles className="w-4 h-4 text-brand-500" />
              If this turns out to need deeper scoping, Orbit flags it and the stakeholder blocks appear here.
            </div>
          </div>
        );
      })()}

      {/* Deep-scoping section blocks — only when flagged for deep scoping */}
      {deep && (
      <div>
        <div className="mb-5">
          <h2 className="text-[17px] font-semibold text-gray-900 tracking-tight">Deep scoping</h2>
          <p className="text-[12.5px] text-gray-400 mt-0.5">The depth this problem needs before a build — fill it yourself, send a block to the right person, or have Orbit call them.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SECTIONS.map(s => {
            const stat = sectionStat(s.letter);
            const pct = stat.total ? Math.round(100 * stat.filled / stat.total) : 0;
            const done = stat.total > 0 && stat.filled >= stat.total;
            const Icon = s.icon;
            return (
              <div key={s.letter} className="bg-brand-50 rounded-2xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${done ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-600'}`}>
                    {done ? <Check size={18} strokeWidth={2.25} /> : <Icon size={18} strokeWidth={2} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[14.5px] text-gray-900">{scope?.section_labels?.[s.letter] || s.letter.toUpperCase()}</span>
                      <span className="text-[10px] font-sans uppercase tracking-wider bg-white text-gray-600 ring-1 ring-gray-200 rounded-full px-2 py-0.5">needs {s.role}</span>
                    </div>
                    <p className="text-[12.5px] text-gray-500 mt-0.5">{s.blurb}</p>
                  </div>
                </div>

                {/* progress */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[11px] text-gray-400 tabular-nums">{stat.filled}/{stat.total || '—'}</span>
                </div>

                {stat.sources.length > 0 && (
                  <div className="text-[11px] text-gray-400 mb-3">Filled by {stat.sources.join(', ')}.</div>
                )}

                {/* collected so far — the actual captured values for this block */}
                {(() => {
                  const caps = sectionCaptured(s.letter);
                  if (caps.length === 0) return null;
                  return (
                    <div className="mb-3 rounded-xl bg-white ring-1 ring-gray-100 divide-y divide-gray-50">
                      {caps.map(c => (
                        <div key={c.label} className="px-3 py-2">
                          <div className="text-[9.5px] font-sans uppercase tracking-wider text-gray-400">{c.label}</div>
                          <div className="text-[12.5px] text-gray-800 leading-snug line-clamp-3 mt-0.5">{c.value}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* actions */}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('orbit:open', { detail: { route: 'problem', context: `problem:${problemId}`, fresh: false } }))} className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-brand-500 text-white hover:bg-brand-600 transition-colors whitespace-nowrap">
                    <Sparkles className="w-3.5 h-3.5" /> Fill with Orbit
                  </button>
                  <button type="button" onClick={() => setShare({ letter: s.letter, role: s.role })} className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white ring-1 ring-gray-200 text-gray-700 hover:ring-brand-300 transition-colors whitespace-nowrap">
                    <Send className="w-3.5 h-3.5" /> Send a link
                  </button>
                  <button type="button" onClick={() => setCallFor({ letter: s.letter, role: s.role })} className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white ring-1 ring-gray-200 text-gray-700 hover:ring-brand-300 transition-colors whitespace-nowrap">
                    <Phone className="w-3.5 h-3.5" /> Schedule a call
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* No page-level Orbit drawer here — the Layout renders ONE global
          OrbitChatDrawer. Rendering a second instance made BOTH respond to
          `orbit:open`, and the Layout's freshProblem one wiped the slot +
          stacked its backdrop (the "panel flashes then hides" bug). The
          header "Scope with Orbit" dispatches orbit:open WITH this problem's
          context so the single global drawer resumes THIS problem. */}

      {share && <ShareModal engagementId={problem.engagement_id!} letter={share.letter} role={share.role} layer={deep ? 'L2' : 'L1'} blocks={deepBlocksFor(share.letter)} phase="L2_PHASE_1" sectionLabel={share.letter === 'e' ? 'this problem' : (scope?.section_labels?.[share.letter] || share.letter.toUpperCase())} onClose={() => { setShare(null); load({ silent: true }); }} />}
      {callFor && <CallModal engagementId={problem.engagement_id!} problemId={problemId} clientId={clientId} letter={callFor.letter} role={callFor.role} blocks={deepBlocksFor(callFor.letter)} phase="L2_PHASE_1" sectionLabel={callFor.letter === 'e' ? 'this problem' : (scope?.section_labels?.[callFor.letter] || callFor.letter.toUpperCase())} onClose={() => { setCallFor(null); load({ silent: true }); }} />}
    </div>
  );
};

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  // Portal to <body>: the page sits inside the Layout, whose transformed/
  // transitioning sidebar ancestor turns `fixed` into ancestor-relative
  // positioning — that's the top gap. Portaling makes the overlay truly
  // viewport-relative so the modal centers on screen.
  createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
);

export const ShareModal: React.FC<any> = ({ engagementId, clientId, letter, role, sectionLabel, layer = 'L2', blocks, phase, onClose }) => {
  // When there are multiple deep-scope blocks, let the user choose to send the
  // whole phase ('*') or one specific block. Defaults to the block they clicked.
  const hasPicker = Array.isArray(blocks) && blocks.length > 1;
  const [sel, setSel] = useState<string>(letter || (hasPicker ? '*' : ''));
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const isAll = sel === '*';
  const issue = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await issueScopeShareToken({
        engagementId,
        // A company section can be shared before there is an engagement; the
        // API picks its address from whichever of these is present.
        clientId,
        section: isAll ? (blocks?.[0]?.letter || letter || 'e') : sel,
        layer,
        recipient_email: email || undefined,
        // "Everything" → one link covering all the listed blocks (works for an
        // L2 phase's blocks OR the company A-D set; the backend resolves the
        // field set from these section letters at the given layer).
        sections: isAll ? (blocks || []).map((b: any) => b.letter) : undefined,
      });
      setLink(window.location.origin + r.share_url);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Couldn't create the link. Try again.");
    } finally { setBusy(false); }
  };
  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand-400';
  return (
    <Modal title="Send a link to a teammate" onClose={onClose}>
      <p className="text-sm text-gray-500 mb-3">
        {isAll
          ? "They'll get one link to fill this whole phase — no login, no other problems."
          : "They'll get a link to fill only this block — no login, no other sections."}
      </p>
      {!link ? (
        <>
          {hasPicker && (
            <>
              <label className="block text-[11px] font-sans uppercase tracking-wider text-gray-400 mb-1">What should they fill?</label>
              <select value={sel} onChange={e => setSel(e.target.value)} className={`${inputCls} bg-white mb-3`}>
                <option value="*">Everything ({blocks.length} blocks)</option>
                {blocks.map((b: any) => (
                  <option key={b.letter} value={b.letter}>{b.label}{b.role ? ` · for the ${b.role}` : ''}</option>
                ))}
              </select>
            </>
          )}
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder={`${(role || 'teammate').toLowerCase()}@company.com`} type="email"
            className={`${inputCls} mb-3`} />
          {err && <p className="mb-2 text-[12px] font-medium text-red-600">{err}</p>}
          <button onClick={issue} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-full text-sm font-semibold hover:bg-brand-600 disabled:opacity-50 whitespace-nowrap">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Create link
          </button>
        </>
      ) : (
        <div>
          <div className="flex items-center gap-2 bg-brand-50 rounded-lg px-3 py-2 text-[12px] text-gray-700 break-all">{link}</div>
          <button onClick={() => navigator.clipboard?.writeText(link)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-full text-sm font-semibold whitespace-nowrap">
            <Copy className="w-4 h-4" /> Copy link
          </button>
        </div>
      )}
    </Modal>
  );
};

// Roles a client can assign (mirrors ClientTeamTab.ROLE_OPTIONS; CONSULTANT is
// locked / auto-assigned and not pickable here).
const TEAM_ROLE_OPTIONS = ['MEMBER', 'ADMIN', 'EXPERT', 'VIEWER'];

export const CallModal: React.FC<any> = ({ engagementId, problemId, clientId, letter, role, sectionLabel, blocks, phase, onClose }) => {
  // 'pick' = call an existing teammate; 'new' = call a new contact (optionally
  // add them to the team with login access).
  const [mode, setMode] = useState<'pick' | 'new'>('pick');
  // Block scope: whole phase ('*') or one specific block. Defaults to the
  // block they clicked. Orbit's call is restricted to exactly this scope.
  const hasPicker = Array.isArray(blocks) && blocks.length > 1;
  const [sel, setSel] = useState<string>(letter || (hasPicker ? '*' : ''));
  const [members, setMembers] = useState<any[]>([]);
  const [canManage, setCanManage] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [newRole, setNewRole] = useState('MEMBER');
  const [addToTeam, setAddToTeam] = useState(true);   // → grants login + invite

  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Load the team roster so the user can call an existing teammate by name +
  // their saved phone. Platform-team rows are filtered out (never dial the
  // AgentsAnywhere team); the linked consultant stays (you may want to call
  // your Solution Partner).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId) { setLoadingMembers(false); setMode('new'); return; }
      try {
        const res = await agentsApi.getWorkspace(clientId);
        if (cancelled) return;
        const list = (res.data?.members || []).filter((m: any) => !m.is_platform_team);
        setMembers(list);
        setCanManage(res.data?.can_manage !== false);
        // If there's nobody with a phone on file, default to the "new contact"
        // flow so the user isn't staring at a phone-less dropdown.
        if (list.length === 0) setMode('new');
      } catch {
        if (!cancelled) { setMembers([]); setMode('new'); }
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const selected = members.find((m: any) => m.email === selectedEmail) || null;
  // Effective phone/name to dial.
  const effPhone = mode === 'pick' ? (phone || selected?.phone || '') : phone;
  const effName = mode === 'pick' ? (selected?.name || selected?.email || '') : name;

  const onPickMember = (em: string) => {
    setSelectedEmail(em);
    const m = members.find((x: any) => x.email === em);
    setPhone(m?.phone || '');   // auto-fill saved phone (blank if none yet)
  };

  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      // 'new' + "add to team" → create the teammate first (this grants a login
      // and, by default, emails the invite). Requires an email. Seat-gated:
      // surface a 402 paywall message rather than failing silently.
      if (mode === 'new' && addToTeam) {
        if (!email.trim()) { setErr('An email is required to add them as a team member.'); setBusy(false); return; }
        try {
          await agentsApi.addWorkspaceMember(clientId, {
            email: email.trim(), name: name || undefined, role: newRole,
            phone: phone || undefined, send_email: true,
          });
        } catch (e: any) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          if (status === 402) {
            setErr(typeof detail === 'object' ? (detail?.message || 'No seats left — upgrade or add a seat to invite a teammate.') : 'No seats left — upgrade or add a seat to invite a teammate.');
          } else if (status === 403) {
            setErr("You don't have permission to add a team member.");
          } else {
            setErr('Could not add the team member. You can still place the call.');
          }
          setBusy(false);
          return;
        }
      }
      // Picked an existing member and typed/changed their number → save it on
      // the member so it auto-fills next time. Best-effort: never block the
      // call if it fails (e.g. non-admin) — the call still goes out.
      if (mode === 'pick' && selected && effPhone && effPhone !== (selected.phone || '')) {
        try {
          await agentsApi.setWorkspaceMemberPhone(clientId, selected.email, effPhone);
          setMembers(prev => prev.map((m: any) => m.email === selected.email ? { ...m, phone: effPhone } : m));
        } catch { /* non-fatal — phone just won't persist this time */ }
      }
      // Always place/schedule the call with the resolved number + name, scoped
      // to the chosen block(s): the whole phase ('*') or one specific block.
      const _sections = sel === '*'
        ? (blocks || []).map((b: any) => b.letter)
        : [sel || letter || 'e'];
      const r = await scheduleSectionCall(engagementId, {
        sections: _sections, problem_id: problemId, to_number: effPhone,
        recipient_name: effName || undefined, scheduled_at: when || undefined,
      });
      setDone(r.status);
    } catch {
      setDone('failed');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = !busy && !!effPhone && (mode === 'pick' ? !!selected : !!name.trim() && (!addToTeam || !!email.trim()));
  // Base (no width) so flex children can size themselves; inputCls adds w-full
  // for stand-alone fields. Mixing w-full with flex-1 broke the email/role row.
  const inputClsBase = 'px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand-400';
  const inputCls = `w-full ${inputClsBase}`;

  return (
    <Modal title={`Orbit calls your team about ${sectionLabel}`} onClose={onClose}>
      {done ? (
        <div className="text-center py-2">
          <div className="w-10 h-10 rounded-full bg-green-50 grid place-items-center mx-auto mb-2"><Check className="w-5 h-5 text-green-600" /></div>
          <p className="text-sm text-gray-700">{done === 'failed' ? 'Could not place the call — check the number.' : when ? 'Call scheduled. Orbit will dial them at that time and capture this for the problem.' : 'Orbit is dialing now. It will explain the problem, ask its questions, and fill it in.'}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-brand-500 text-white rounded-full text-sm font-semibold">Done</button>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">Orbit calls them, explains this problem, asks the questions, and fills it in.</p>

          {/* Block scope — call about the whole phase or one specific block. */}
          {hasPicker && (
            <div className="mb-3">
              <label className="block text-[11px] font-sans uppercase tracking-wider text-gray-400 mb-1">What should Orbit cover?</label>
              <select value={sel} onChange={e => setSel(e.target.value)} className={`${inputCls} bg-white`}>
                <option value="*">Everything ({blocks.length} blocks)</option>
                {blocks.map((b: any) => (
                  <option key={b.letter} value={b.letter}>{b.label}{b.role ? ` · for the ${b.role}` : ''}</option>
                ))}
              </select>
            </div>
          )}

          {/* Mode toggle — pick a teammate or add a new contact. */}
          {members.length > 0 && (
            <div className="inline-flex bg-brand-50 rounded-full p-[3px] gap-0.5 mb-3">
              <button type="button" onClick={() => setMode('pick')}
                className={`whitespace-nowrap h-8 px-3.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-colors ${mode === 'pick' ? 'bg-black text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                <UsersIcon className="w-3.5 h-3.5" /> Team member
              </button>
              <button type="button" onClick={() => setMode('new')}
                className={`whitespace-nowrap h-8 px-3.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-colors ${mode === 'new' ? 'bg-black text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                <UserPlus className="w-3.5 h-3.5" /> New contact
              </button>
            </div>
          )}

          {loadingMembers ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading your team…</div>
          ) : mode === 'pick' ? (
            <>
              <label className="block text-[11px] font-sans uppercase tracking-wider text-gray-400 mb-1">Who should Orbit call?</label>
              <select value={selectedEmail} onChange={e => onPickMember(e.target.value)} className={`${inputCls} mb-2 bg-white`}>
                <option value="">Select a team member…</option>
                {members.map((m: any) => (
                  <option key={m.email} value={m.email}>
                    {(m.name || m.email)}{m.role ? ` · ${m.role}` : ''}{m.phone ? '' : ' · no phone on file'}
                  </option>
                ))}
              </select>
              {selected && (
                <>
                  <label className="block text-[11px] font-sans uppercase tracking-wider text-gray-400 mt-2 mb-1">
                    Phone number {selected.phone ? '' : '(none on file — enter it)'}
                  </label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. +1 555 000 0000" inputMode="tel"
                    className={`${inputCls} mb-1 ${!phone ? 'border-amber-300' : ''}`} />
                  {!selected.phone && (
                    <p className="text-[11px] text-amber-600 mb-2">No number saved for {selected.name || selected.email} yet — type it once and Orbit saves it for next time.</p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className={`${inputCls} mb-2`} />
              <label className="block text-[11px] font-sans uppercase tracking-wider text-gray-400 mb-1">Phone number</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. +1 555 000 0000" inputMode="tel" className={`${inputCls} mb-2 ${!phone ? 'border-amber-300' : ''}`} />
              {/* Add-as-team-member toggle → grants login + saves their phone. */}
              {canManage && (
                <label className="flex items-start gap-2 my-2 cursor-pointer select-none">
                  <input type="checkbox" checked={addToTeam} onChange={e => setAddToTeam(e.target.checked)} className="mt-0.5 accent-brand-500" />
                  <span className="text-[12.5px] text-gray-700">
                    <span className="inline-flex items-center gap-1 font-medium text-gray-900"><KeyRound className="w-3.5 h-3.5 text-brand-500" /> Add to the team with login access</span>
                    <span className="block text-gray-400 text-[11.5px]">Creates their account, saves their phone, and emails an invite. Leave off to just call them once.</span>
                  </span>
                </label>
              )}
              {addToTeam && canManage && (
                <div className="flex gap-2 mb-2">
                  <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="email@company.com" className={`${inputClsBase} flex-1 min-w-0`} />
                  <select value={newRole} onChange={e => setNewRole(e.target.value)} className={`${inputClsBase} w-[116px] shrink-0 bg-white`}>
                    {TEAM_ROLE_OPTIONS.map(r => <option key={r} value={r}>{r[0] + r.slice(1).toLowerCase()}</option>)}
                  </select>
                </div>
              )}
            </>
          )}

          <input value={when} onChange={e => setWhen(e.target.value)} type="datetime-local" className={`${inputCls} mt-1 mb-2`} />
          <p className="text-[11px] text-gray-400 mb-3">Leave the time empty to call now.</p>

          {err && <p className="text-[12px] text-red-600 mb-2">{err}</p>}

          {/* Tell the user WHY the button is disabled — the phone placeholder
              looks like a value, so a blank number reads as "filled". */}
          {!canSubmit && !busy && (
            <p className="text-[11.5px] text-gray-400 mb-2">
              {mode === 'pick' && !selected ? 'Select a team member to call.'
                : !effPhone ? 'Enter a phone number to call.'
                : mode === 'new' && !name.trim() ? 'Enter their name.'
                : mode === 'new' && addToTeam && !email.trim() ? 'Add an email to create their team account, or uncheck "Add to the team".'
                : 'Add the missing details to continue.'}
            </p>
          )}

          <button onClick={go} disabled={!canSubmit} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-full text-sm font-semibold hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} {when ? 'Schedule call' : 'Call now'}
          </button>
        </>
      )}
    </Modal>
  );
};

export default ProblemDetail;
